/**
 * OrderSummary.tsx — the ONE look of an order summary, shared by the plan
 * screen's rail (`PlanSelection`) and the card step's rail (`PaymentRail`).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: checkout dedupe
 * (2026-09-26).
 *
 * WHY (the CTO, reviewing stage on a phone and a laptop): "in the plan
 * selection screen and … the next screen where you enter the delivery
 * information and card … it looks slightly different … try to align to the
 * same in both screens as much as possible". The two summaries had been built
 * a month apart and each carried its own copy of the same idea: the plan
 * screen's figure was 24px at weight 800 (a SYNTHESISED bold — `main.tsx`
 * bundles Fraunces at 400 and 600 only), the card step's 34px at 600; one row
 * had a space between label and value and the other did not; the eyebrows were
 * two hand-copied style objects. They now draw every piece from here, so they
 * cannot drift apart again.
 *
 * WHAT IS SHARED, AND WHAT IS NOT. The card, the eyebrow, the figure's type,
 * the plan name + term, and the muted label/value rows are shared. The
 * CONTENT is each screen's own and is not decided here: the plan screen leads
 * with the monthly price of the plan being built; the card step leads with
 * what meets the card TODAY ("Held on your card today" / "Paid today", CLAUDE.md
 * "TWO COLUMNS, AND A RAIL WHERE TODAY LEADS"). Every figure is passed in
 * already formatted — this module does no arithmetic and prints no money of its
 * own.
 *
 * ⚠️ HEIGHT IS BUDGETED ON THE PLAN SCREEN. Its summary is the rail's pinned
 * head at ≥880px, and every pixel it grows comes out of the scrolling
 * agreement list at 1280×720 (PlanSelection's rail note). So the figure has a
 * `size`: the card step's lead figure is 34px, the plan screen's sits on the
 * plan row at 24px, beside the plan name and term on ONE line (measured at
 * 1280×720 in Chrome: every line this card gains is a line the agreement
 * list's reading window loses). Same face, weight and numerals — one family, not one size.
 *
 * Inline styles, like the rest of the checkout's small components, so it renders
 * the same inside and outside `.gps-journey-root`.
 */
import type { CSSProperties, ReactNode } from 'react';

/**
 * The summary card: the journey's one white card (`gps-jux-panel` — tokens.css
 * P7 paints it white with the soft card shadow under the journey root). A real,
 * opaque surface: on the card step it is the sticky part of the rail and
 * content scrolls beneath it.
 */
export const orderSummaryCardStyle: CSSProperties = {
  background: 'var(--gps-surface)',
  border: '1px solid var(--gps-border)',
  borderRadius: 'var(--gps-r-lg)',
  padding: 'var(--gps-s-4)',
};

/**
 * The small uppercase label that names a summary panel — "Order summary", and
 * the plan screen's "What you're agreeing to". Styled as a label so the figure
 * below stays the biggest thing in the panel.
 */
export const summaryEyebrowStyle: CSSProperties = {
  margin: '0 0 12px',
  fontFamily: 'var(--gps-font-body)',
  fontSize: '12px',
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  color: 'var(--gps-muted)',
};

/**
 * The eyebrow as an element. `as="h2"` where it names a region (the card
 * step's summary is a `section` labelled by it); a plain `p` elsewhere.
 */
export function SummaryEyebrow({ as = 'p', id, children }: { as?: 'p' | 'h2'; id?: string; children: ReactNode }) {
  const Tag = as;
  return (
    <Tag id={id} style={summaryEyebrowStyle}>
      {children}
    </Tag>
  );
}

/** The money figure's type: display face at 600 (bundled, never synthesised), tabular numerals. */
function figureStyle(size: 'lead' | 'row'): CSSProperties {
  return {
    margin: 0,
    fontFamily: 'var(--gps-font-display)',
    fontSize: size === 'lead' ? '34px' : '24px',
    fontWeight: 600,
    lineHeight: 1.15,
    color: 'var(--gps-text)',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
  };
}

/** The muted words after a figure — "(your first month)", "/month". */
const figureSuffixStyle: CSSProperties = {
  fontFamily: 'var(--gps-font-body)',
  fontSize: '14px',
  fontWeight: 600,
  color: 'var(--gps-muted)',
};

/**
 * A money figure and the words that qualify it, in one line. `suffix` is
 * separated by a real space, so the line reads (and is announced) as
 * "£45 (your first month)", never "£45(your first month)".
 */
export function SummaryFigure({
  figure,
  suffix,
  size = 'lead',
  as = 'p',
}: {
  figure: string;
  suffix?: string;
  size?: 'lead' | 'row';
  as?: 'p' | 'span';
}) {
  const Tag = as;
  return (
    <Tag style={{ ...figureStyle(size), display: as === 'span' ? 'inline' : 'block' }}>
      {figure}
      {suffix ? (
        <>
          {' '}
          <span style={figureSuffixStyle}>{suffix}</span>
        </>
      ) : null}
    </Tag>
  );
}

/** The plan's name, bold, with its term under (or beside) it, muted. */
export function SummaryPlanName({ name, term, inline = false }: { name: string; term: string | null; inline?: boolean }) {
  return (
    <span
      style={{
        display: inline ? 'flex' : 'block',
        alignItems: 'baseline',
        flexWrap: 'wrap',
        gap: inline ? '0 8px' : undefined,
        minWidth: 0,
        lineHeight: 1.4,
      }}
    >
      <span style={{ display: 'block', fontSize: '15px', fontWeight: 700, color: 'var(--gps-text)' }}>{name}</span>
      {term ? (
        <>
          {inline ? ' ' : null}
          <span style={{ fontSize: '13.5px', color: 'var(--gps-muted)', whiteSpace: 'nowrap' }}>{term}</span>
        </>
      ) : null}
    </span>
  );
}

/** The ruled-off block the muted rows sit in. */
export const summaryRowsStyle: CSSProperties = {
  marginTop: 'var(--gps-s-3)',
  borderTop: '1px solid var(--gps-border)',
  paddingTop: '6px',
};

/**
 * One muted label/value row — "3-month total, billed monthly  £535.95",
 * "Delivery  Free". The two halves are separated by a real space, so the row
 * reads "Delivery Free" as text and to a screen reader (the plan screen's row
 * always had one; the card step's did not).
 */
export function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 'var(--gps-s-3)',
        padding: '4px 0',
        fontSize: '13.5px',
        lineHeight: 1.4,
        color: 'var(--gps-muted)',
      }}
    >
      <span>{label}</span>{' '}
      <span style={{ fontWeight: 600, textAlign: 'right', whiteSpace: 'nowrap' }}>{value}</span>
    </div>
  );
}

/** The term, as both summaries print it: "1 month", "3 months". */
export function termLabel(months: number): string {
  return `${months} month${months === 1 ? '' : 's'}`;
}

/**
 * The term-total row's label, as both summaries print it: "3-month total,
 * billed monthly". ONE definition, so the plan screen and the card step cannot
 * word the same row two ways.
 *
 * "BILLED MONTHLY" STAYS IN THE LABEL: it is what stops the total reading as one
 * charge. SHORTER THAN "Total over 3 months, billed monthly" (review,
 * 2026-09-26) because at 880–1199px both rails are 20rem, and there the long
 * label wrapped to a second line inside the plan rail's PINNED head — measured in
 * Chrome at 1024×768, one of the lines the agreement's reading window lost (it
 * fell to 10px). This one fits the rail's 276px row with the figure beside it.
 */
export function termTotalLabel(months: number): string {
  return `${months}-month total, billed monthly`;
}
